/* DietLanding — SEO landing page for `/diet/:slug` (Gluten-Free, Vegan, etc.)
 *
 * Each of the 11 dietary requirements the Family Recipes tool supports
 * gets its own topical hub page — great for Google to index "gluten free
 * recipes" style searches, and for cooks with dietary needs to browse
 * every safe recipe in one place. Powered by /api/public/diet/{slug}.
 *
 * SEO rich-objects shipped per page:
 *   • <title> + <meta description>              → via PageMeta title/description
 *   • <meta name="keywords">                    → topical cluster per diet
 *   • <link rel="canonical">                    → recipefiler.com/diet/<slug>
 *   • og:/twitter card + og:image               → via PageMeta
 *   • CollectionPage JSON-LD w/ ItemList        → up to 200 recipes
 *   • BreadcrumbList JSON-LD                    → Home → Diets → <diet>
 *   • FAQPage JSON-LD (safety FAQ)              → prohibited ingredients
 *   • <h1> + <h2> headings                      → "<Diet> recipes" + "What's safe"
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import RecipeCard from "@/components/RecipeCard";
import PageMeta from "@/components/PageMeta";
import { recipePath } from "@/lib/slug";
import { ShieldCheck } from "@phosphor-icons/react";

const SITE_ORIGIN = "https://recipefiler.com";

// Topical keyword clusters per diet. These power the <meta name=
// "keywords"> tag that Bing, Yandex and AI search crawlers still read.
// Google ignores keywords since 2009 but the overhead is zero.
const DIET_KEYWORDS = {
  vegan: "vegan recipes, plant-based recipes, dairy-free vegan meals, egg-free vegan, vegan dinner ideas, vegan baking",
  vegetarian: "vegetarian recipes, meatless meals, vegetarian dinner ideas, vegetarian baking",
  "gluten-free": "gluten-free recipes, coeliac recipes, celiac-safe meals, wheat-free cooking, GF baking",
  "dairy-free": "dairy-free recipes, lactose-free meals, no-milk cooking, lactose intolerant recipes, DF baking",
  "egg-free": "egg-free recipes, egg allergy safe, no-egg baking, egg-free dinners",
  "nut-free": "nut-free recipes, peanut allergy safe, school-safe lunches, tree-nut-free baking",
  "soy-free": "soy-free recipes, no-soy meals, soy allergy safe cooking",
  "low-fodmap": "low FODMAP recipes, IBS-friendly meals, FODMAP diet dinners, FODMAP baking",
  "low-histamine": "low histamine recipes, histamine intolerance meals, HIT-safe cooking",
  "diabetic-friendly": "diabetic recipes, type 2 diabetes meals, low-carb diabetic baking, blood-sugar-friendly cooking",
  "renal-friendly": "renal diet recipes, kidney-friendly meals, low-potassium cooking, dialysis diet",
};

export default function DietLanding() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setData(null); setError(null);
    api.get(`/public/diet/${slug}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? "not-found" : "error"));
  }, [slug]);

  // Build the per-page JSON-LD schemas. Memoised against `data` so we
  // don't rebuild on every re-render. Shipped through PageMeta's
  // `jsonLd` prop so Emergent's crawler-cache captures them in the
  // sync-render DOM snapshot BEFORE useEffect fires.
  const jsonLd = useMemo(() => {
    if (!data) return undefined;
    const recipes = data.recipes || [];
    const prohibitedShort = (data.prohibited || "").split(",").slice(0, 3).map((s) => s.trim()).filter(Boolean);
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
          { "@type": "ListItem", position: 2, name: "Special-diet recipes", item: `${SITE_ORIGIN}/diet` },
          { "@type": "ListItem", position: 3, name: `${data.label} recipes`, item: `${SITE_ORIGIN}/diet/${data.slug}` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: `${data.label} recipes`,
        url: `${SITE_ORIGIN}/diet/${data.slug}`,
        description: recipes.length === 0
          ? `${data.label} recipes on Recipe Filer — safety-first, metric measurements, family-tested.`
          : `${recipes.length} ${data.label.toLowerCase()} ${recipes.length === 1 ? "recipe" : "recipes"} preserved on Recipe Filer. Every one is drafted with strict safety rules${prohibitedShort.length ? ` — no ${prohibitedShort.join(", ")}${data.prohibited.includes(",") ? " and more" : ""}` : ""}.`,
        inLanguage: "en",
        isPartOf: { "@type": "WebSite", name: "Recipe Filer", url: SITE_ORIGIN },
        mainEntity: {
          "@type": "ItemList",
          name: `${data.label} recipes`,
          numberOfItems: recipes.length,
          itemListElement: recipes.slice(0, 200).map((r, idx) => ({
            "@type": "ListItem",
            position: idx + 1,
            url: `${SITE_ORIGIN}${recipePath(r)}`,
            name: r.title,
          })),
        },
      },
    ];
    if (data.prohibited) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: `What is prohibited in a ${data.label.toLowerCase()} diet?`,
            acceptedAnswer: {
              "@type": "Answer",
              text: `A ${data.label.toLowerCase()} diet excludes: ${data.prohibited}.`,
            },
          },
          {
            "@type": "Question",
            name: `Are Recipe Filer's ${data.label.toLowerCase()} recipes safe?`,
            acceptedAnswer: {
              "@type": "Answer",
              text: `Every ${data.label.toLowerCase()} recipe on Recipe Filer is drafted with the strict safety rules shown on this page, uses metric measurements, and lists safe swaps where helpful. Always cross-check against your own medical advice.`,
            },
          },
        ],
      });
    }
    return schemas;
  }, [data]);

  if (error === "not-found") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h1 className="font-display text-4xl font-black text-rf-ink mb-4">Diet not found</h1>
        <p className="text-rf-ink-soft mb-6">We couldn't find a diet page for &ldquo;{slug}&rdquo;.</p>
        <Link to="/diet" className="btn-amber">Browse all diets</Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-rf-orange border-t-transparent rounded-full animate-spin" aria-hidden="true" />
      </div>
    );
  }

  const recipes = data.recipes || [];

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-20" data-testid={`diet-landing-${data.slug}`}>
      <PageMeta
        title={`${data.label} recipes — ${recipes.length} safe, family-tested dishes`}
        description={`Browse ${recipes.length} ${data.label.toLowerCase()} ${recipes.length === 1 ? "recipe" : "recipes"} on Recipe Filer. Every dish is drafted with strict safety rules and metric measurements, ready to cook.`}
        keywords={DIET_KEYWORDS[data.slug] || `${data.label.toLowerCase()} recipes, ${data.label.toLowerCase()} dinners, ${data.label.toLowerCase()} baking`}
        canonical={`/diet/${data.slug}`}
        ogImage={recipes[0]?.ai_image_url || recipes[0]?.images?.[0]}
        jsonLd={jsonLd}
      />
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <Link to="/diet" className="text-sm font-bold text-rf-orange hover:underline">← All dietary requirements</Link>
      </div>
      <p className="overline mb-3">Dietary requirement</p>
      <h1 className="font-display text-5xl md:text-7xl font-black text-rf-ink mb-6 leading-[0.95]">
        {data.label} recipes
      </h1>
      <p className="text-rf-ink-soft text-lg mb-6 max-w-3xl">
        {recipes.length === 0
          ? `No public ${data.label.toLowerCase()} recipes yet — check back soon.`
          : `${recipes.length} ${recipes.length === 1 ? "recipe" : "recipes"} that follow ${data.label.toLowerCase()} guidelines, drafted with strict safety rules. Metric measurements throughout.`}
      </p>

      {/* Safety card — surfaces the diet's prohibited-ingredient spec so
          cooks (and search engines) can see exactly what "safe" means. */}
      <div
        className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5 mb-12 max-w-3xl"
        data-testid="diet-safety-card"
      >
        <div className="flex items-start gap-3">
          <ShieldCheck size={22} weight="duotone" className="text-emerald-700 flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="font-display font-black text-emerald-900 text-sm mb-1">Safety rules for every {data.label} recipe</h2>
            <p className="text-sm text-emerald-900/90 mb-2">
              <b>Never includes:</b> {data.prohibited}
            </p>
            <p className="text-sm text-emerald-900/80 mb-1">
              <b>How we adapt:</b> {data.safety_notes}
            </p>
            <p className="text-xs text-emerald-900/70 mt-2">
              Always check every packet label — manufacturer recipes change. Medical diets are not a substitute for professional advice.
            </p>
          </div>
        </div>
      </div>

      {recipes.length > 0 ? (
        <>
          <h2 className="font-display font-black text-2xl text-rf-ink mb-4" data-testid="diet-recipes-heading">
            All {data.label.toLowerCase()} recipes ({recipes.length})
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="diet-recipe-grid">
            {recipes.map((r) => (<RecipeCard key={r.recipe_id} recipe={r} />))}
          </div>
        </>
      ) : (
        <div className="card-recipe text-center py-12">
          <p className="text-rf-ink-soft mb-4">Try the full directory or browse another diet.</p>
          <Link to="/directory" className="btn-amber">Browse all recipes</Link>
        </div>
      )}
    </div>
  );
}
